import React, { useState, useRef, useEffect, forwardRef } from 'react';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Select from '@/components/form/Select';
import TextArea from '@/components/form/input/TextArea';
import Button from '@/components/ui/button/Button';
import { CreateCarModelDto } from '../api/carModelsApi';
import { useTechProfiles } from '../hooks/useCarModels';
import { findTechProfileIdByBodyType } from '../utils/techProfileMatching';
import { withFormProtection } from '@/components/form/withFormProtection';
import { useToast } from '@/components/ui/toast/Toast';
import ImagePreview from '@/components/ui/images/ImagePreview';
import { useTranslation } from 'react-i18next';

const BODY_TYPE_OPTIONS = [
  { value: 'sedan', label: 'Sedan' },
  { value: 'coupe', label: 'Coupe' },
  { value: 'suv', label: 'SUV' },
  { value: 'electric', label: 'Electric' }
];

const TRANSMISSION_TYPE_OPTIONS = [
  { value: 'auto', label: 'Automatic' },
  { value: 'manual', label: 'Manual' }
];

interface FormErrors {
  slug?: string;
  brand_name?: string;
  model_name?: string;
  transmission_type?: string;
  body_type?: string;
}

interface CarModelCreateFormProps {
  onSubmit: (data: CreateCarModelDto, image?: File) => Promise<void>;
  onClose: (hasUnsavedChanges?: boolean) => void;
  initialData: CreateCarModelDto;
  currentData?: CreateCarModelDto;
  onDataChange?: (data: CreateCarModelDto) => void;
}

const defaultFormData: CreateCarModelDto = {
  slug: '',
  brand_name: '',
  model_name: '',
  transmission_type: 'auto',
  body_type: 'sedan',
  tech_profile: '',
  description_en: '',
  description_fr: '',
  is_active: true
};

const CarModelCreateForm = forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarModelCreateFormProps>(
  ({ onSubmit, onClose, initialData, onDataChange }, ref) => {
    const { t } = useTranslation();
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { data: techProfilesResponse } = useTechProfiles();
    const techProfiles = techProfilesResponse?.data || [];
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const [formData, setFormData] = useState<CreateCarModelDto>(initialData || defaultFormData);

    useEffect(() => {
      onDataChange?.(formData);
    }, [formData, onDataChange]);

    const generateSlug = (brandName: string, modelName: string) => {
      return `${brandName}-${modelName}`
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
    };

    const validateForm = (): boolean => {
      const newErrors: FormErrors = {};

      if (!formData.slug.trim()) {
        newErrors.slug = 'Slug is required';
      }
      if (!formData.brand_name.trim()) {
        newErrors.brand_name = t('car_models.create.errors.brand_name_required');
      }
      if (!formData.model_name.trim()) {
        newErrors.model_name = t('car_models.create.errors.model_name_required');
      }
      if (!formData.transmission_type) {
        newErrors.transmission_type = t('car_models.create.errors.transmission_type_required');
      }
      if (!formData.body_type) {
        newErrors.body_type = t('car_models.create.errors.body_type_required');
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const clearFieldError = (field: keyof FormErrors) => {
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

    const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, slug: value }));
      if (value.trim()) {
        clearFieldError('slug');
      }
    };

    const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({
        ...prev,
        brand_name: value,
        slug: formData.slug || generateSlug(value, formData.model_name)
      }));
      if (value.trim()) {
        clearFieldError('brand_name');
      }
    };

    const handleModelNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({
        ...prev,
        model_name: value,
        slug: formData.slug || generateSlug(formData.brand_name, value)
      }));
      if (value.trim()) {
        clearFieldError('model_name');
      }
    };

    const handleTransmissionTypeChange = (value: string) => {
      setFormData(prev => ({ ...prev, transmission_type: value }));
      if (value) {
        clearFieldError('transmission_type');
      }
    };

    const handleBodyTypeChange = (value: string) => {
      setFormData(prev => ({ ...prev, body_type: value }));
      if (value) {
        clearFieldError('body_type');
      }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedImage(file);
      }
    };

    const handleImageClick = () => {
      fileInputRef.current?.click();
    };

    const handleRemoveImage = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      try {
        const selectedBodyTypeOption = BODY_TYPE_OPTIONS.find((option) => option.value === formData.body_type);
        const techProfileId = findTechProfileIdByBodyType(
          formData.body_type,
          techProfiles,
          selectedBodyTypeOption?.label
        );

        await onSubmit({
          ...formData,
          tech_profile: techProfileId ?? formData.tech_profile,
        }, selectedImage || undefined);
        toast.success(t('car_models.create.success'));
      } catch (error) {
        toast.error(t('car_models.create.error'));
      }
    };

    return (
      <>
        <div className="px-6 py-4 border-b border-border-primary">
          <h5 className="text-xl font-semibold text-text-primary lg:text-2xl">
            {t('car_models.create.title')}
          </h5>
          <p className="text-sm text-text-tertiary">
            {t('car_models.create.description')}
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="col-span-2">
              <Label>{t('car_models.create.fields.image')}</Label>
              <div className="mt-2 flex items-center gap-4">
                <div
                  className="relative w-32 h-32 border-[1px] border-gray-300 rounded-lg overflow-hidden cursor-pointer"
                  onClick={handleImageClick}
                >
                  {selectedImage ? (
                    <div className="w-full h-full">
                      <div
                        className="w-full h-full flex"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <ImagePreview imageUrl={URL.createObjectURL(selectedImage)}>
                          <img
                            src={URL.createObjectURL(selectedImage)}
                            alt={t('car_models.create.image_alt')}
                            className="w-full h-full object-cover"
                          />
                        </ImagePreview>
                      </div>
                      <button
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 z-10 bg-bg-secondary rounded-full p-1 shadow-md hover:bg-bg-hover"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="relative w-32 h-32">
                      <div className="absolute inset-0 flex items-center justify-center bg-bg-tertiary bg-opacity-50 text-text-muted">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>

            <div className="col-span-2">
              <Label>{t('car_models.create.fields.slug')}</Label>
              <Input
                testId="car-model-form-field-slug"
                type="text"
                value={formData.slug}
                onChange={handleSlugChange}
                placeholder={t('car_models.create.placeholders.enter_slug')}
                error={errors.slug}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.create.fields.brand_name')} *</Label>
              <Input
                testId="car-model-form-field-brand_name"
                type="text"
                value={formData.brand_name}
                onChange={handleBrandNameChange}
                placeholder={t('car_models.create.placeholders.enter_brand_name')}
                error={errors.brand_name}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.create.fields.model_name')} *</Label>
              <Input
                testId="car-model-form-field-model_name"
                type="text"
                value={formData.model_name}
                onChange={handleModelNameChange}
                placeholder={t('car_models.create.placeholders.enter_model_name')}
                error={errors.model_name}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.create.fields.transmission_type')} *</Label>
              <Select
                testId="car-model-form-field-transmission_type"
                options={TRANSMISSION_TYPE_OPTIONS}
                onChange={handleTransmissionTypeChange}
                defaultValue={formData.transmission_type}
                placeholder={t('car_models.create.placeholders.select_transmission')}
                error={errors.transmission_type}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.create.fields.body_type')} *</Label>
              <Select
                testId="car-model-form-field-body_type"
                options={BODY_TYPE_OPTIONS}
                onChange={handleBodyTypeChange}
                defaultValue={formData.body_type}
                placeholder={t('car_models.create.placeholders.select_body_type')}
                error={errors.body_type}
              />
            </div>

            <div className="col-span-2">
              <Label>{t('car_models.create.fields.description_en')}</Label>
              <TextArea
                testId="car-model-form-field-description_en"
                value={formData.description_en}
                onChange={(value) => setFormData(prev => ({ ...prev, description_en: value }))}
                placeholder={t('car_models.create.placeholders.enter_description_en')}
                rows={4}
              />
            </div>

            <div className="col-span-2">
              <Label>{t('car_models.create.fields.description_fr')}</Label>
              <TextArea
                testId="car-model-form-field-description_fr"
                value={formData.description_fr}
                onChange={(value) => setFormData(prev => ({ ...prev, description_fr: value }))}
                placeholder={t('car_models.create.placeholders.enter_description_fr')}
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border-primary">
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => onClose()}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              testId="car-model-form-submit-button"
              variant="primary"
              onClick={handleSubmit}
              disabled={Object.keys(errors).length > 0}
            >
              {t('car_models.create.buttons.create')}
            </Button>
          </div>
        </div>
      </>
    );
  }
);

CarModelCreateForm.displayName = 'CarModelCreateForm';

const ProtectedCarModelCreateForm = withFormProtection(CarModelCreateForm);

interface CarModelCreateFormWrapperProps extends Omit<CarModelCreateFormProps, 'currentData' | 'onDataChange' | 'initialData'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const CarModelCreateFormWrapper = forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarModelCreateFormWrapperProps>((props, ref) => {
  const [currentData, setCurrentData] = useState<CreateCarModelDto>(defaultFormData);

  const handleDataChange = (data: CreateCarModelDto) => {
    setCurrentData(data);
  };

  return (
    <ProtectedCarModelCreateForm
      {...props}
      ref={ref}
      initialData={defaultFormData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

CarModelCreateFormWrapper.displayName = 'CarModelCreateFormWrapper';

export default CarModelCreateFormWrapper;
